import axios from "axios";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import {
  getSalaryAdjustmentMutations,
  type SalaryAdjustment,
} from "./adjustSalary.logic";

interface SaveParams {
  readonly adjustments: readonly SalaryAdjustment[];
  readonly initialAdjustments: readonly SalaryAdjustment[];
  readonly salaryNoticeId: number;
  readonly workerId: number;
}

interface PersistAdjustmentsParams {
  readonly companyId: number;
  readonly mutations: readonly SalaryAdjustment[];
  readonly salaryNoticeId: number;
  readonly workerId: number;
}

export type AdjustSalaryStatus =
  | { readonly type: "idle" }
  | { readonly type: "submitting" }
  | { readonly type: "success" }
  | { readonly type: "error"; readonly message: string };

interface UseAdjustSalaryReturn {
  readonly status: AdjustSalaryStatus;
  readonly save: (params: SaveParams) => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getApiErrorMessage(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const responseData: unknown = error.response?.data;
  if (!isRecord(responseData)) return undefined;
  const messages = [
    responseData.errorMessage,
    responseData.message,
    responseData.Message,
  ];
  return messages.find(
    (message): message is string => typeof message === "string",
  );
}

async function persistAdjustments({
  companyId,
  mutations,
  salaryNoticeId,
  workerId,
}: PersistAdjustmentsParams): Promise<void> {
  for (const mutation of mutations) {
    await Api().company.salary.putAdditionalRow(companyId, workerId, {
      count: 1,
      key: mutation.key,
      salaryNoticeId,
      value: mutation.value,
    });
  }
  await Api().company.salary.regenerateSalaryNotice(
    companyId,
    workerId,
    salaryNoticeId,
  );
}

export const useAdjustSalary = (companyId: number): UseAdjustSalaryReturn => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<AdjustSalaryStatus>({ type: "idle" });
  const isSavingRef = useRef(false);

  const save = ({
    adjustments,
    initialAdjustments,
    salaryNoticeId,
    workerId,
  }: SaveParams): void => {
    const mutations = getSalaryAdjustmentMutations({
      adjustments,
      initialAdjustments,
    });
    if (isSavingRef.current || mutations.length === 0) return;
    isSavingRef.current = true;
    setStatus({ type: "submitting" });

    const saveAdjustments = async (): Promise<void> => {
      try {
        await persistAdjustments({
          companyId,
          mutations,
          salaryNoticeId,
          workerId,
        });
        setStatus({ type: "success" });
      } catch (error) {
        const message =
          getApiErrorMessage(error) ?? String(t("Alerts.SomethingWentWrong"));
        setStatus({ type: "error", message });
      } finally {
        isSavingRef.current = false;
      }
    };

    void saveAdjustments();
  };
  return { save, status };
};
